import React, { useState, useEffect } from 'react';
// WO-24 — needed by the pending-invitation card, which sends the person to the
// page that accepts it.
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlanGate, getPlanGate } from '../lib/planGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import { toast } from 'sonner';
import { OnboardingEmptyState } from '../onboarding';
import {
  Users, UserPlus, Crown, Shield, Copy, LogOut, Trash2, ArrowRightLeft, Loader2, Link as LinkIcon, AlertCircle,
  PauseCircle, CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroupPage() {
  const { user, token } = useAuth();
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();  // WO-24
  const [loading, setLoading] = useState(true);
  const [groupData, setGroupData] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { action, target }
  const planGate = usePlanGate();

  const txt = {
    title: language === 'ar' ? 'الفريق' : 'Team',
    subtitle:
      language === 'ar'
        ? 'شارك جميع بيانات CRM الخاصة بك، بما في ذلك الفرص وجهات الاتصال والتنبيهات مع أعضاء فريقك.'
        : 'Share all your CRM, including Opportunities, Contacts and Alerts with your team.',
    // WO-21 — spell the two rules out where the Team is set up, so nobody has
    // to discover them by being refused later.
    rules:
      language === 'ar'
        ? 'يرى كل عضو في الفريق جميع الفرص وجهات الاتصال التي أنشأها أي عضو. ويحذف كل عضو ما أنشأه فقط — أما مسؤول الفريق فيمكنه حذف ما أنشأه أي عضو من فريقه. والمشاركة متاحة لخطة Premium أو لمقعد Enterprise: من تغيّرت خطته يبقى مكانه في الفريق لكنه يرى ما أنشأه هو فقط حتى يشترك من جديد.'
        : 'Everyone in the Team sees every Opportunity and Contact created by any member. Each member can delete only what they created — the Team Admin can delete entries created by any member of their Team. Sharing comes with Premium or an Enterprise seat: a member whose plan changes keeps their place in the Team but sees only their own entries until they subscribe again.',
    noGroup: language === 'ar' ? 'لست في أي فريق' : 'You are not in a Team',
    create: language === 'ar' ? 'إنشاء فريق' : 'Create Team',
    createDesc: language === 'ar' ? 'أنشئ فريقًا لمشاركة الفرص وجهات الاتصال والتنبيهات.' : 'Create a team to share Opportunities, Contacts and Alerts.',
    members: language === 'ar' ? 'الأعضاء' : 'Members',
    invite: language === 'ar' ? 'دعوة عضو' : 'Invite Member',
    inviteDesc: language === 'ar' ? 'أنشئ رابط دعوة لمشاركته. صالح لمدة 7 أيام.' : 'Create an invite link to share. Valid for 7 days.',
    inviteEmail: language === 'ar' ? 'البريد الإلكتروني (اختياري)' : 'Restrict to email (optional)',
    generate: language === 'ar' ? 'إنشاء رابط' : 'Generate link',
    copy: language === 'ar' ? 'نسخ' : 'Copy',
    copied: language === 'ar' ? 'تم النسخ' : 'Copied',
    kick: language === 'ar' ? 'إخراج' : 'Remove',
    transfer: language === 'ar' ? 'نقل دور مسؤول الفريق' : 'Transfer Team Admin',
    leave: language === 'ar' ? 'مغادرة الفريق' : 'Leave team',
    name: language === 'ar' ? 'اسم الفريق' : 'Team name',
    upgradeRequired: language === 'ar' ? 'متاح فقط لخطط Premium و Enterprise' : 'Available on Premium and Enterprise plans only',
    you: language === 'ar' ? 'أنت' : 'You',
    admin: language === 'ar' ? 'مسؤول الفريق' : 'Team Admin',
  };

  const fetchGroup = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to load your Team');
      setGroupData(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroup(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    if (!groupName.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: groupName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        const pg = getPlanGate(data);
        if (pg) { setCreateOpen(false); planGate.show(pg); return; }
        throw new Error(data?.detail || 'Create failed');
      }
      toast.success(language === 'ar' ? 'تم إنشاء الفريق' : 'Team created');
      setCreateOpen(false);
      setGroupName('');
      fetchGroup();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups/${groupData.group.id}/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invited_email: inviteEmail.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) {
        const pg = getPlanGate(data);
        if (pg) { setInviteOpen(false); planGate.show(pg); return; }
        throw new Error(data?.detail || 'Invite failed');
      }
      // Build a frontend URL based on current origin (override backend's URL)
      const frontUrl = `${window.location.origin}/group-invite/${data.invitation.token}`;
      setInviteResult({ ...data.invitation, url: frontUrl });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(txt.copied);
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleKick = async (memberId) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups/${groupData.group.id}/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Kick failed');
      toast.success(language === 'ar' ? 'تم إخراج العضو' : 'Member removed');
      fetchGroup();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleTransfer = async (memberId) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups/${groupData.group.id}/transfer-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_admin_user_id: memberId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Transfer failed');
      toast.success(language === 'ar' ? 'تم نقل دور المسؤول' : 'Admin transferred');
      fetchGroup();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleLeave = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups/${groupData.group.id}/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Leave failed');
      toast.success(language === 'ar' ? 'لقد غادرت الفريق' : 'You left the team');
      setGroupData({ ...groupData, group: null });
      fetchGroup();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  // BUG-INV-04: a group admin (incl. a sole admin) can dissolve the group.
  const handleDeleteGroup = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups/${groupData.group.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Delete failed');
      toast.success(language === 'ar' ? 'تم حذف الفريق' : 'Team deleted');
      setGroupData({ ...groupData, group: null });
      fetchGroup();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const planAllowsCreate = groupData?.can_create;
  // WO-22 — why you are not in a Team any more, and where the Admin role went.
  const notices = groupData?.notices || {};
  const isGroupAdmin = groupData?.is_group_admin;
  const group = groupData?.group;
  // WO-23 — the Team you still belong to but cannot use because your plan
  // lapsed. The backend sends this INSTEAD of the Team itself, so a paused
  // member never receives their colleagues' names or e-mail addresses.
  const pausedTeam = groupData?.paused_team;
  // WO-24 — invitations addressed to me. Before this there was nowhere in the
  // product that told somebody an invitation was waiting: if the email did not
  // arrive, the invitation was undiscoverable.
  const pendingInvitations = groupData?.pending_invitations || [];

  return (
    <div className={`max-w-4xl mx-auto space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Users className="w-8 h-8 text-primary" />
          {txt.title}
        </h1>
        <p className="text-muted-foreground mt-1">{txt.subtitle}</p>
        {/* WO-21 — who sees what and who can delete what, said once, here. */}
        <p className="text-sm text-muted-foreground mt-2 max-w-3xl" data-testid="team-rules">
          {txt.rules}
        </p>
      </div>

      {/* WO-23 — your Team is PAUSED, not gone. The card has to answer three
          things at once: what stopped working, that nothing was deleted, and
          that paying again is all it takes — no invitation, no re-sharing. When
          nobody in the Team is paying it is the whole Team that is suspended,
          and the Team Admin is told how to bring it back. */}
      {pausedTeam && (
        <Card
          className="border-amber-300 dark:border-amber-700 bg-amber-50/70 dark:bg-amber-950/30"
          data-testid="team-paused-notice"
        >
          <CardContent className="pt-6">
            <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
              <PauseCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm space-y-1">
                <p className="font-semibold">
                  {pausedTeam.suspended
                    ? (language === 'ar'
                        ? `فريق «${pausedTeam.name || ''}» موقوف مؤقتًا`
                        : `Your Team "${pausedTeam.name || ''}" is suspended`)
                    : (language === 'ar'
                        ? `عضويتك في فريق «${pausedTeam.name || ''}» موقوفة مؤقتًا`
                        : `Your membership of the Team "${pausedTeam.name || ''}" is paused`)}
                </p>
                <p>
                  {pausedTeam.suspended
                    ? (language === 'ar'
                        ? 'لا أحد من أعضاء الفريق على خطة Premium أو على مقعد Enterprise حاليًا، لذلك توقّفت المشاركة: كل عضو يرى ما أنشأه هو فقط.'
                        : 'Nobody in the Team is currently on Premium or holding an Enterprise seat, so sharing has stopped: each member sees only what they created themselves.')
                    : (language === 'ar'
                        ? 'مشاركة الفريق متاحة لخطة Premium أو لمقعد Enterprise. عند تغيّر خطتك توقّفت مشاركتك: لم يعد زملاؤك يرون ما أنشأته، وأنت ترى الآن ما أنشأته أنت فقط.'
                        : 'Team sharing comes with Premium, or with an Enterprise seat. When your plan changed your sharing stopped: your colleagues no longer see the contacts and opportunities you created, and you now see only your own.')}
                </p>
                <p className="font-medium">
                  {language === 'ar'
                    ? 'لم يُحذف شيء ولم يُخرَج أحد. ما زلت ضمن الفريق، ويعود كل شيء تلقائيًا بمجرد الاشتراك من جديد — دون دعوة جديدة ودون إعادة مشاركة أي سجل.'
                    : 'Nothing has been deleted and nobody has been removed. You keep your place, and everything comes back automatically the moment you subscribe again — no new invitation, no records to re-share.'}
                </p>
                {pausedTeam.suspended && pausedTeam.is_admin && (
                  <p>
                    {language === 'ar'
                      ? `أنت مسؤول هذا الفريق ويبقى الدور لك: ${pausedTeam.member_count} عضوًا وسجلاتهم كما هي، ويستأنف الفريق عمله بمجرد أن يشترك أي عضو من أعضائه.`
                      : `You are still its Team Admin and the role stays yours: ${pausedTeam.member_count} ${pausedTeam.member_count === 1 ? 'member' : 'members'} and their records are untouched, and the Team resumes as soon as any one of you subscribes.`}
                  </p>
                )}
                {!pausedTeam.suspended && pausedTeam.paying_member_count > 0 && (
                  <p>
                    {language === 'ar'
                      ? 'الفريق ما زال يعمل مع بقية الأعضاء المشتركين.'
                      : 'The Team itself is still running with the members who are subscribed.'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WO-24 — somebody has invited you. This is the whole fix for an
          invitation that nobody could see: the Team page now says so, whether
          or not the email arrived, and sends you to the page that accepts it. */}
      {pendingInvitations.length > 0 && (
        <Card
          className="border-primary/40 bg-primary/5"
          data-testid="pending-invitations"
        >
          <CardContent className="pt-6 space-y-3">
            {pendingInvitations.map((inv) => (
              <div
                key={inv.token}
                className={`flex items-start justify-between gap-3 flex-wrap ${isRTL ? 'flex-row-reverse text-right' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <UserPlus className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div className="text-sm space-y-1">
                    <p className="font-semibold">
                      {language === 'ar'
                        ? `لديك دعوة للانضمام إلى فريق «${inv.group_name}»`
                        : `You have been invited to join the Team "${inv.group_name}"`}
                    </p>
                    <p className="text-muted-foreground">
                      {inv.invited_by_name
                        ? (language === 'ar'
                            ? `دعاك ${inv.invited_by_name}.`
                            : `${inv.invited_by_name} invited you.`)
                        : ''}
                      {' '}
                      {language === 'ar'
                        ? 'مشاركة الفريق متاحة لخطة Premium أو لمقعد Enterprise.'
                        : 'Team sharing comes with Premium or an Enterprise seat.'}
                    </p>
                    {inv.expires_at && (
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar' ? 'تنتهي الصلاحية في' : 'Expires'}{' '}
                        {new Date(inv.expires_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/group-invite/${inv.token}`)}
                  data-testid={`open-invitation-${inv.token}`}
                >
                  {language === 'ar' ? 'عرض الدعوة' : 'View invitation'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* WO-23 — the other half of the same story: they paid, and the Team came
          back by itself. Worth saying out loud, once. */}
      {notices.team_resumed && (
        <Card
          className="border-emerald-300 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30"
          data-testid="team-resumed-notice"
        >
          <CardContent className="pt-6">
            <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm">
                {language === 'ar'
                  ? `عدت إلى فريق «${notices.team_resumed.group_name || ''}»: استُؤنفت المشاركة، وعادت سجلاتك إلى CRM الفريق وعادت سجلات زملائك إليك.`
                  : `You are back in the Team "${notices.team_resumed.group_name || ''}". Sharing has resumed: your records are in the Team CRM again, and your colleagues' records are back in your lists.`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WO-22 — the Admin role moved because the previous Admin's plan lapsed. */}
      {(notices.team_admin_gained || notices.team_admin_lost) && (
        <Card data-testid="team-admin-change-notice">
          <CardContent className="pt-6">
            <div className={`flex items-start gap-3 text-sm ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
              <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <p>
                {notices.team_admin_gained
                  ? (language === 'ar'
                      ? `أصبحت مسؤول فريق «${notices.team_admin_gained.group_name || ''}»: انتقل الدور إليك تلقائيًا لأن خطة المسؤول السابق لم تعد سارية، ولأنك أقدم عضو مشترك.`
                      : `You are now the Team Admin of "${notices.team_admin_gained.group_name || ''}". The role passed to you automatically because the previous Admin's plan lapsed and you are the longest-standing member still subscribed.`)
                  : (language === 'ar'
                      ? `لم تعد مسؤول فريق «${notices.team_admin_lost.group_name || ''}»: انتقل الدور إلى عضو مشترك عندما تغيّرت خطتك.`
                      : `You are no longer the Team Admin of "${notices.team_admin_lost.group_name || ''}". The role passed to a subscribed member when your plan changed.`)}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No group case. WO-23 — not shown to a paused member: they HAVE a Team,
          and inviting them to create another one would contradict the card
          above (and the backend refuses it while they are on a roster). */}
      {!group && !pausedTeam && (
        <Card data-testid="no-group-card">
          <CardHeader>
            <CardTitle>{txt.noGroup}</CardTitle>
            <CardDescription>{txt.createDesc}</CardDescription>
          </CardHeader>
          <CardContent>
            {planAllowsCreate ? (
              <Button onClick={() => setCreateOpen(true)} data-testid="create-group-btn">
                <Users className="w-4 h-4 mr-2" />
                {txt.create}
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
                <AlertCircle className="w-4 h-4" />
                {txt.upgradeRequired}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active group */}
      {group && (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="flex items-center gap-2" data-testid="group-name">
                    {group.name}
                    {isGroupAdmin && (
                      <Badge className="bg-primary text-white gap-1">
                        <Crown className="w-3 h-3" /> {txt.admin}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {/* WO-24 — "1 members" was showing on a Team of one. */}
                    {group.member_count} {(group.member_count === 1
                      ? (language === 'ar' ? 'عضو' : 'member')
                      : (language === 'ar' ? 'أعضاء' : 'members'))} · {groupData.plan_id}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {isGroupAdmin && (
                    <Button onClick={() => { setInviteOpen(true); setInviteResult(null); setInviteEmail(''); }} data-testid="invite-btn">
                      <UserPlus className="w-4 h-4 mr-2" />
                      {txt.invite}
                    </Button>
                  )}
                  {isGroupAdmin && (
                    <Button
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setConfirm({ action: 'delete' })}
                      data-testid="delete-group-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> {language === 'ar' ? 'حذف الفريق' : 'Delete team'}
                    </Button>
                  )}
                  {!isGroupAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => setConfirm({ action: 'leave' })}
                      data-testid="leave-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> {txt.leave}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* WO-23 — members whose plan lapsed. They are still on the
                  roster, so the Team Admin is told why entries went missing AND
                  that there is nothing for him to do about it. */}
              {(group.paused_members || []).length > 0 && (
                <div
                  className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/70 dark:bg-amber-950/30 p-3 text-xs"
                  data-testid="team-paused-members"
                >
                  <div className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <PauseCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                    <div>
                      <p className="font-semibold">
                        {language === 'ar' ? 'عضويتهم موقوفة مؤقتًا بعد تغيّر خطتهم:' : 'Paused after their plan changed:'}
                      </p>
                      <p>
                        {(group.paused_members || []).map(x => x.name || x.email).join(', ')}
                      </p>
                      <p className="mt-1">
                        {language === 'ar'
                          ? 'اختفت السجلات التي أنشأوها من CRM الفريق ولم يُحذف منها شيء. يبقى مكانهم في الفريق، ويعودون تلقائيًا عند الاشتراك من جديد — لا حاجة إلى دعوة جديدة.'
                          : 'The records they created have left the Team CRM, and none of them have been deleted. They keep their place, and they come back automatically when they subscribe again — no new invitation needed.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {group.members.map((m) => {
                const isMe = m.user_id === user?.id;
                return (
                  <div
                    key={m.user_id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${
                      m.qualifies === false
                        ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800'
                        : 'bg-card hover:bg-muted/30'
                    }`}
                    data-testid={`member-row-${m.user_id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-sm font-semibold">
                        {(m.name || m.email || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-2 flex-wrap">
                          {m.name || '—'}
                          {isMe && <Badge variant="outline" className="text-[10px]">{txt.you}</Badge>}
                          {m.is_admin && (
                            <Badge variant="default" className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] gap-1">
                              <Shield className="w-3 h-3" /> {txt.admin}
                            </Badge>
                          )}
                          {m.plan_id && (
                            <Badge variant="outline" className="text-[10px] capitalize" data-testid={`member-plan-${m.user_id}`}>
                              {m.plan_id}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                        {m.qualifies === false && (
                          <div className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1 mt-1" data-testid={`member-lapsed-${m.user_id}`}>
                            <AlertCircle className="w-3 h-3 shrink-0" />
                            {language === 'ar'
                              ? 'موقوف مؤقتًا: لم يعد على خطة Premium — لا يرى بيانات الفريق ولا يراها الفريق منه، ويعود تلقائيًا عند الاشتراك'
                              : 'Paused: no longer on Premium — neither sees the Team\'s shared data nor shares their own, and returns automatically on subscribing'}
                          </div>
                        )}
                      </div>
                    </div>
                    {isGroupAdmin && !isMe && (
                      <div className="flex items-center gap-2">
                        {!m.is_admin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirm({ action: 'transfer', target: m })}
                            data-testid={`transfer-btn-${m.user_id}`}
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
                            {txt.transfer}
                          </Button>
                        )}
                        {!m.is_admin && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setConfirm({ action: 'kick', target: m })}
                            data-testid={`kick-btn-${m.user_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            {txt.kick}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* WO-33 — a Team of one, seen by its admin (onboarding switch on). */}
              {isGroupAdmin && group.member_count === 1 && language !== 'ar' && (
                <OnboardingEmptyState
                  kind="team"
                  variant="inline"
                  onPrimary={() => { setInviteOpen(true); setInviteResult(null); setInviteEmail(''); }}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Create group dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{txt.create}</DialogTitle>
            <DialogDescription>{txt.createDesc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="groupName">{txt.name}</Label>
            <Input
              id="groupName"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={language === 'ar' ? 'فريق المبيعات' : 'Sales Team'}
              data-testid="group-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={busy}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleCreate} disabled={busy || !groupName.trim()} data-testid="confirm-create-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              {txt.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) setInviteResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{txt.invite}</DialogTitle>
            <DialogDescription>{txt.inviteDesc}</DialogDescription>
          </DialogHeader>
          {!inviteResult ? (
            <div className="space-y-2">
              <Label htmlFor="invEmail">{txt.inviteEmail}</Label>
              <Input
                id="invEmail"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="someone@your-company.com"
                type="email"
                data-testid="invite-email-input"
              />
              <p className="text-xs text-muted-foreground">
                {language === 'ar'
                  ? 'إذا قمت بإدخال بريد إلكتروني، سيتم إرسال رابط الدعوة تلقائيًا.'
                  : 'If you provide an email, the invite link is emailed automatically.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'رابط الدعوة' : 'Invite URL'}</Label>
              <div className="flex items-center gap-2">
                <Input value={inviteResult.url} readOnly data-testid="invite-url-input" />
                <Button onClick={() => copy(inviteResult.url)} variant="outline" size="icon" data-testid="copy-invite-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              {/* WO-24 — with no address, nothing has been sent and nobody has
                  been told: it is a link the Team Admin has to pass on. The
                  dialog used to show a bare URL and leave that to be guessed. */}
              {!inviteResult.invited_email && (
                <div
                  className="flex items-start gap-2 text-xs rounded-md p-2 border bg-muted/50"
                  data-testid="invite-link-only-note"
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    {language === 'ar'
                      ? 'لم تحدّد بريدًا إلكترونيًا، لذلك لم يُرسل شيء ولم يُبلَّغ أحد: انسخ هذا الرابط وأرسله بنفسك. أي شخص من شركتك لديه الرابط يمكنه الانضمام.'
                      : 'You did not give an email address, so nothing has been sent and nobody has been told — copy this link and pass it on yourself. Anyone in your company who has the link can join.'}
                  </span>
                </div>
              )}
              {inviteResult.invited_email && (
                <div
                  className={`flex items-center gap-2 text-xs rounded-md p-2 border ${
                    inviteResult.email_sent
                      ? 'bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-700 text-green-800 dark:text-green-300'
                      : 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300'
                  }`}
                  data-testid="invite-email-status"
                >
                  {inviteResult.email_sent
                    ? (language === 'ar'
                        ? `تم إرسال البريد الإلكتروني إلى ${inviteResult.invited_email}.`
                        : `Email sent to ${inviteResult.invited_email}.`)
                    : (language === 'ar'
                        ? `لم يتم إرسال البريد إلى ${inviteResult.invited_email} (شارك الرابط يدويًا).`
                        : `Couldn't email ${inviteResult.invited_email} — please share the link manually.`)}
                  {/* WO-24 — and say when the person was also told inside the
                      app, because that is the delivery that does not depend on
                      an inbox. */}
                  {inviteResult.notified_in_app && (language === 'ar'
                    ? ' وتم إبلاغه أيضًا داخل التطبيق.'
                    : ' They have also been notified inside the app.')}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {language === 'ar' ? 'تنتهي الصلاحية في' : 'Expires'} {new Date(inviteResult.expires_at).toLocaleString()}
              </p>
            </div>
          )}
          <DialogFooter>
            {!inviteResult ? (
              <>
                <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={busy}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={handleInvite} disabled={busy} data-testid="generate-invite-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
                  {txt.generate}
                </Button>
              </>
            ) : (
              <Button onClick={() => setInviteOpen(false)} variant="default">
                {language === 'ar' ? 'تم' : 'Done'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog */}
      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === 'kick' && (language === 'ar' ? 'إخراج العضو؟' : 'Remove member?')}
              {confirm?.action === 'transfer' && (language === 'ar' ? 'نقل دور مسؤول الفريق؟' : 'Transfer the Team Admin role?')}
              {confirm?.action === 'leave' && (language === 'ar' ? 'مغادرة الفريق؟' : 'Leave the team?')}
              {confirm?.action === 'delete' && (language === 'ar' ? 'حذف الفريق؟' : 'Delete the team?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === 'kick' && (
                language === 'ar'
                  ? `سيتم إخراج ${confirm.target?.name} من الفريق. لن يتمكنوا من الوصول إلى البيانات المشتركة.`
                  : `${confirm.target?.name} will be removed from the team and lose access to shared data.`
              )}
              {confirm?.action === 'transfer' && (
                language === 'ar'
                  ? `سيصبح ${confirm.target?.name} مسؤول الفريق. ستفقد صلاحية دعوة الأعضاء وإخراجهم وحذف ما أنشأه أعضاء فريقك، وتبقى عضوًا عاديًا. يمكن للفريق أن يكون له مسؤول واحد فقط.`
                  : `${confirm.target?.name} becomes the Team Admin. You lose the right to invite and remove members and to delete entries created by your Team members, and stay an ordinary member. A Team can have only one Admin.`
              )}
              {confirm?.action === 'leave' && (
                language === 'ar'
                  ? 'لن تتمكن من رؤية CRM المشترك أو التنبيهات بعد المغادرة.'
                  : 'You will lose access to shared CRM and alerts after leaving.'
              )}
              {confirm?.action === 'delete' && (
                language === 'ar'
                  ? 'سيتم حلّ الفريق نهائيًا. سيفقد جميع الأعضاء الوصول إلى البيانات المشتركة. لا يمكن التراجع عن هذا الإجراء.'
                  : 'The team will be permanently dissolved. All members lose access to shared data. This cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm.action === 'kick') handleKick(confirm.target.user_id);
                if (confirm.action === 'transfer') handleTransfer(confirm.target.user_id);
                if (confirm.action === 'leave') handleLeave();
                if (confirm.action === 'delete') handleDeleteGroup();
              }}
              disabled={busy}
              data-testid="confirm-action-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {language === 'ar' ? 'تأكيد' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
